import _ from 'lodash-es'
import Token from './auth-token-wrapper'
import { API_WRAPPER } from './axios-wrapper'
import { ApiInstanceKeys } from './api-instances'
import { ENDPOINTS } from './endpoints'
import { ApiResponse, IKycStatus, IKycUrl } from '../typeScript/interfaces/IKyc'
import { IErrorResponse } from 'typeScript/interfaces/IErrorMessage'

const getDataAsURLParams = (data) => {
  const params = new URLSearchParams()

  for (const [key, value] of Object.entries(data)) {
    if (typeof value === 'string') {
      params.append(key, value)
    }
  }

  return params
}

const parseObjectToQueryString = (objectQuery) => {
  let queryString

  if (!_.isEmpty(objectQuery)) {
    queryString = Object.keys(objectQuery)
      .map((key) => `${key}=${objectQuery[key]}`)
      .join('&')
    return queryString
  }
  return ''
}

const API = {
  products: {
    getProducts(queryObject) {
      const queryString = parseObjectToQueryString(queryObject)
      return API_WRAPPER[ApiInstanceKeys.ONBOARDING].callGet(
        `${ENDPOINTS.ONBOARDING.PRODUCTS}?${queryString}`
      )
    },
  },
  assets: {
    getRFIDAssets() {
      return API_WRAPPER[ApiInstanceKeys.ASSET_MANAGEMENT].callGet(
        `${ENDPOINTS.ASSET_MANAGEMENT.RFID_ASSETS}`
      )
    },
    updateRFIDStatus(id) {
      return API_WRAPPER[ApiInstanceKeys.ASSET_MANAGEMENT].callPost(
        `${ENDPOINTS.ASSET_MANAGEMENT.ASSETS}/${id}/activate`
      )
    },
    getShellToken(visualNumber: string) {
      return API_WRAPPER[ApiInstanceKeys.ASSET_MANAGEMENT].callGet(
        `${ENDPOINTS.ASSET_MANAGEMENT.SHELL_TOKEN}/${visualNumber}`
      )
    },
    activateShellToken(visualNumber: string) {
      return API_WRAPPER[ApiInstanceKeys.ASSET_MANAGEMENT].callPost(
        `${ENDPOINTS.ASSET_MANAGEMENT.SHELL_TOKEN}/${visualNumber}/activate`
      )
    },
  },
  auth: {
    refreshToken() {
      const refreshToken = Token.getRefreshToken()

      const finalData = {
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
      }

      return API_WRAPPER[ApiInstanceKeys.AUTHORIZATION].callPost(
        `${ENDPOINTS.AUTHORIZATION.TOKEN}`,
        getDataAsURLParams(finalData)
      )
    },
  },
  leads: {
    saveLeadData(params) {
      return API_WRAPPER[ApiInstanceKeys.ONBOARDING].callPost(
        `${ENDPOINTS.ONBOARDING.ACCOUNT}`,
        params
      )
    },
    createLead(params, queryObject) {
      const queryString = parseObjectToQueryString(queryObject)
      return API_WRAPPER[ApiInstanceKeys.ONBOARDING].callPost(
        `${ENDPOINTS.ONBOARDING.NEW_LEAD}?${queryString}`,
        params
      )
    },

    saveProducts(params) {
      return API_WRAPPER[ApiInstanceKeys.ONBOARDING].callPost(
        `${ENDPOINTS.ONBOARDING.USER_PRODUCTS}`,
        params
      )
    },
    saveQuestionsAnswers(params) {
      return API_WRAPPER[ApiInstanceKeys.ONBOARDING].callPost(
        `${ENDPOINTS.ONBOARDING.USER_QUESTIONS}`,
        params
      )
    },
    getLeadStatus() {
      return API_WRAPPER[ApiInstanceKeys.ONBOARDING].callGet(
        `${ENDPOINTS.ONBOARDING.USER_STATUS}`
      )
    },
    confirmEmployeeCustomer(params) {
      return API_WRAPPER[ApiInstanceKeys.ONBOARDING].callPost(
        `${ENDPOINTS.ONBOARDING.CONFIRM_EMPLOYEE}`,
        params
      )
    },
  },
  installation: {
    getInstallationData() {
      return API_WRAPPER[ApiInstanceKeys.ONBOARDING].callGet(
        `${ENDPOINTS.ONBOARDING.INSTALLATIONS}`
      )
    },
    updateInstallationData(params) {
      return API_WRAPPER[ApiInstanceKeys.ONBOARDING].callPost(
        `${ENDPOINTS.ONBOARDING.INSTALLATIONS}`,
        params
      )
    },
    getInstallationEvent() {
      return API_WRAPPER[ApiInstanceKeys.ONBOARDING].callGet(
        `${ENDPOINTS.ONBOARDING.INSTALLATION_EVENTS}`
      )
    },
    getInstallationStatus() {
      return API_WRAPPER[ApiInstanceKeys.ONBOARDING].callGet(
        `${ENDPOINTS.ONBOARDING.INSTALLATION_STATUS}`
      )
    },
    uploadInstallationPhoto(params) {
      return API_WRAPPER[ApiInstanceKeys.ONBOARDING].callPost(
        `${ENDPOINTS.ONBOARDING.FILES}`,
        params
      )
    },
    getUploadedInstallationPhotos() {
      return API_WRAPPER[ApiInstanceKeys.ONBOARDING].callGet(
        `${ENDPOINTS.ONBOARDING.FILES}`
      )
    },
    getUploadedInstallationPhoto(id) {
      return API_WRAPPER[ApiInstanceKeys.ONBOARDING].callGet(
        `${ENDPOINTS.ONBOARDING.FILES}/` + id
      )
    },
  },
  payment: {
    getPaymentAmount() {
      return API_WRAPPER[ApiInstanceKeys.ONBOARDING].callGet(
        `${ENDPOINTS.ONBOARDING.PAYMENT_AMOUNT}`
      )
    },
    getPaymentMethods(params) {
      return API_WRAPPER[ApiInstanceKeys.ONBOARDING].callPost(
        `${ENDPOINTS.ONBOARDING.PAYMENT_METHODS}`,
        params
      )
    },
    doPayment(params) {
      return API_WRAPPER[ApiInstanceKeys.ONBOARDING].callPost(
        `${ENDPOINTS.ONBOARDING.DO_PAYMENT}`,
        params
      )
    },
    submitAdditionalDetails(params) {
      return API_WRAPPER[ApiInstanceKeys.ONBOARDING].callPost(
        `${ENDPOINTS.ONBOARDING.PAYMENT_DETAILS}`,
        params
      )
    },
    getPaymentStatus() {
      return API_WRAPPER[ApiInstanceKeys.ONBOARDING].callGet(
        `${ENDPOINTS.ONBOARDING.PAYMENT_STATUS}`
      )
    },
    confirmOrder(params) {
      return API_WRAPPER[ApiInstanceKeys.ONBOARDING].callPost(
        `${ENDPOINTS.ONBOARDING.CONFIRM_ORDER}`,
        params
      )
    },
    getB2bEmployeeSetup(code) {
      return API_WRAPPER[ApiInstanceKeys.ONBOARDING].callGet(
        `${ENDPOINTS.ONBOARDING.B2B_EMPLOYEE_SETUP}/` + code
      )
    },
  },
  payout: {
    payoutEnergyTariffRegistration(energyTariffCreateRequest, contractProof) {
      const bodyFormData = new FormData()
      bodyFormData.append(
        'energyTariffCreateRequest',
        new Blob([JSON.stringify(energyTariffCreateRequest)], {
          type: 'application/json',
        })
      )
      bodyFormData.append('contractProof', contractProof, contractProof.name)
      const customConfig = { 'Content-Type': 'multipart/form-data;' }

      return API_WRAPPER[ApiInstanceKeys.CUSTOMER_LIFE_CYCLE].callPost(
        `${ENDPOINTS.CUSTOMER_LIFE_CYCLE.PAYOUT_ENERGY_TARIFF}`,
        bodyFormData,
        customConfig
      )
    },
    getPayoutHistory() {
      return API_WRAPPER[ApiInstanceKeys.CUSTOMER_LIFE_CYCLE].callGet(
        `${ENDPOINTS.CUSTOMER_LIFE_CYCLE.PAYOUT_ENERGY_TARIFF}`
      )
    },
    payoutIBANRegistration(params) {
      return API_WRAPPER[ApiInstanceKeys.PAYAUT].callPost(
        `${ENDPOINTS.PAYAUT_SERVICE.ACCOUNT}`,
        params
      )
    },
    getPayoutIBANInfo() {
      return API_WRAPPER[ApiInstanceKeys.PAYAUT].callGet(
        `${ENDPOINTS.PAYAUT_SERVICE.PAYOUT_IBAN}`
      )
    },
    getKYCUrl(): Promise<ApiResponse<IKycUrl> | ApiResponse<IErrorResponse>> {
      return API_WRAPPER[ApiInstanceKeys.PAYAUT].callGet(
        `${ENDPOINTS.PAYAUT_SERVICE.KYC_URL}`
      )
    },
    getKYCStatus(): Promise<
      ApiResponse<IKycStatus> | ApiResponse<IErrorResponse>
    > {
      return API_WRAPPER[ApiInstanceKeys.PAYAUT].callGet(
        `${ENDPOINTS.PAYAUT_SERVICE.KYC_STATUS}`
      )
    },
  },
  fleetData: {
    getFleetData() {
      return API_WRAPPER[ApiInstanceKeys.CUSTOMER_LIFE_CYCLE].callGet(
        `${ENDPOINTS.CUSTOMER_LIFE_CYCLE.FLEET_DATA}`
      )
    },
    fleetDataUpdate(params) {
      return API_WRAPPER[ApiInstanceKeys.CUSTOMER_LIFE_CYCLE].callPut(
        `${ENDPOINTS.CUSTOMER_LIFE_CYCLE.FLEET_DATA}`,
        params
      )
    },
  },
  user: {
    hasRecurringPaymentDetails() {
      return API_WRAPPER[ApiInstanceKeys.ONBOARDING].callGet(
        `${ENDPOINTS.ONBOARDING.HAS_RECURRING_PAYMENT_DETAILS}`
      )
    },
    getLoggedUser() {
      return API_WRAPPER[ApiInstanceKeys.AUTHORIZATION].callGet(
        `${ENDPOINTS.AUTHORIZATION.ME}`
      )
    },
    getBillingData() {
      return API_WRAPPER[ApiInstanceKeys.ONBOARDING].callGet(
        `${ENDPOINTS.ONBOARDING.PAYMENTS}`
      )
    },
    updateBillingData(params) {
      return API_WRAPPER[ApiInstanceKeys.ONBOARDING].callPost(
        `${ENDPOINTS.ONBOARDING.PAYMENTS}`,
        params
      )
    },
    updatePassword(passwordData) {
      return API_WRAPPER[ApiInstanceKeys.AUTHORIZATION].callPost(
        `${ENDPOINTS.AUTHORIZATION.CHANGE_PASSWORD}`,
        passwordData
      )
    },
    setPassword(passwordData) {
      return API_WRAPPER[ApiInstanceKeys.AUTHORIZATION].callPost(
        `${ENDPOINTS.AUTHORIZATION.SET_PASSWORD}`,
        passwordData
      )
    },
    getTariffs() {
      return API_WRAPPER[ApiInstanceKeys.ONBOARDING].callGet(
        `${ENDPOINTS.ONBOARDING.TARIFFS}`
      )
    },
    getInvoices() {
      return API_WRAPPER[ApiInstanceKeys.ONBOARDING].callGet(
        `${ENDPOINTS.ONBOARDING.INVOICES}`
      )
    },
    getInvoice(id) {
      return API_WRAPPER[ApiInstanceKeys.ONBOARDING].callGet(
        `${ENDPOINTS.ONBOARDING.INVOICES}/${id}`
      )
    },
    getInvoiceFile(invoiceId, fileId) {
      return API_WRAPPER[ApiInstanceKeys.ONBOARDING].callGet(
        `${ENDPOINTS.ONBOARDING.INVOICES}/${invoiceId}/files/${fileId}`
      )
    },
    getContracts() {
      return API_WRAPPER[ApiInstanceKeys.ONBOARDING].callGet(
        `${ENDPOINTS.ONBOARDING.CONTRACTS}`
      )
    },
    getContract(id) {
      return API_WRAPPER[ApiInstanceKeys.ONBOARDING].callGet(
        `${ENDPOINTS.ONBOARDING.CONTRACTS}/${id}`
      )
    },
    getQuotes() {
      return API_WRAPPER[ApiInstanceKeys.ONBOARDING].callGet(
        `${ENDPOINTS.ONBOARDING.QUOTES}`
      )
    },
    getQuote(id) {
      return API_WRAPPER[ApiInstanceKeys.ONBOARDING].callGet(
        `${ENDPOINTS.ONBOARDING.QUOTES}/${id}`
      )
    },
    getQuoteFile(quoteId, fileId) {
      return API_WRAPPER[ApiInstanceKeys.ONBOARDING].callGet(
        `${ENDPOINTS.ONBOARDING.QUOTES}/${quoteId}/files/${fileId}`
      )
    },
    getProducts() {
      return API_WRAPPER[ApiInstanceKeys.ONBOARDING].callGet(
        `${ENDPOINTS.ONBOARDING.USER_PRODUCTS}`
      )
    },
    getAccount() {
      return API_WRAPPER[ApiInstanceKeys.ONBOARDING].callGet(
        `${ENDPOINTS.ONBOARDING.USER_INFO}`
      )
    },
    updateUserInfo(params) {
      return API_WRAPPER[ApiInstanceKeys.ONBOARDING].callPatch(
        `${ENDPOINTS.ONBOARDING.UPDATE_USER_INFO}`,
        params
      )
    },
  },
  charger: {
    getChargers(platformCustomerId: string) {
      return API_WRAPPER[ApiInstanceKeys.CHARGER_MANAGEMENT].callGet(
        `${ENDPOINTS.CHARGER_MANAGEMENT.CHARGERS}/customer/${platformCustomerId}/chargers`
      )
    },
    getCharger(id) {
      return API_WRAPPER[ApiInstanceKeys.CHARGER_MANAGEMENT].callGet(
        `${ENDPOINTS.CHARGER_MANAGEMENT.CHARGER}/${id}`
      )
    },
    updateChargerName(id, params) {
      return API_WRAPPER[ApiInstanceKeys.CHARGER_MANAGEMENT].callPatch(
        `${ENDPOINTS.CHARGER_MANAGEMENT.CHARGER}/${id}`,
        params
      )
    },
  },
  support: {
    getSupportTopics() {
      return API_WRAPPER[ApiInstanceKeys.ONBOARDING].callGet(
        `${ENDPOINTS.ONBOARDING.SUBJECTS}`
      )
    },
    saveSupportRequest(params) {
      return API_WRAPPER[ApiInstanceKeys.ONBOARDING].callPost(
        `${ENDPOINTS.ONBOARDING.SUBJECTS_ME}`,
        params
      )
    },
  },
  documents: {
    getDocuments() {
      return API_WRAPPER[ApiInstanceKeys.DOCUMENT_MANAGEMENT].callGet(
        `${ENDPOINTS.DOCUMENTS_MANAGER.DOCUMENT}`
      )
    },
    getDocumentById(id) {
      return API_WRAPPER[ApiInstanceKeys.DOCUMENT_MANAGEMENT].callGet(
        `${ENDPOINTS.DOCUMENTS_MANAGER.DOCUMENT}/${id}`
      )
    },
  },
  contactForm: {
    cancellation(params) {
      return API_WRAPPER[ApiInstanceKeys.CONTACT_FORM].callPost(
        `${ENDPOINTS.CONTACT_FORM.CANCELLATION}`,
        params
      )
    },
  },
}

export default API
